import type { JSX, ReactNode } from 'react';
import { cx } from './cx';

/**
 * The module family — a port of `ui/components/Module.kt`.
 *
 * A module is the dashboard's only container: a panel of translucent glass over
 * the page gradient, edged with a hairline and carrying **no shadow**. Depth
 * comes from what shows through, not from a drop shadow — on a midnight
 * background a shadow only produces grey haze, and a module over the bright
 * centre of the glow is meant to read slightly lighter than one at the edge.
 *
 * Every rule that is a *look* lives in `src/theme/components.css` under the
 * matching class name; what lives here is the structure and the decisions.
 * Nothing in this file sets a font size or a colour literal.
 *
 * ### Tinting a value is a class, not a prop
 *
 * The blocks that own a colour (`.stat-cell__value`, `.label-value-row__value`)
 * read `var(--value-color, <default>)`, and the `.c-*` utilities at the top of
 * `components.css` are what feed it — so a caller writes
 * `className="c-dead"` and the number goes red without anything here
 * hard-coding the palette. That is the convention `components.css` documents,
 * and it replaces the `valueColor` prop this file used to carry: a token string
 * such as `'var(--alive)'` written into the element's inline `style`, which
 * needed a cast at every call site because `CSSProperties` is generated from
 * csstype and has no index signature for custom properties.
 *
 * `--value-color` inherits, which is why the utility can sit on the cell rather
 * than on the one element that shows the colour: the only descendant reading the
 * property is the value, and both labels (`.stat-cell__label`,
 * `.label-value-row__label`) pin their own secondary colour. Put a `.c-*` on a
 * whole *module* and that stops being true — it tints every variable-reading
 * descendant inside it.
 */

/**
 * The glass panel itself.
 *
 * A plain `<div>`, not a `<section>`: a section with no accessible name is
 * exposed as a generic container anyway, and half the modules on the dashboard
 * are a bare row of numbers with no heading to name them.
 */
export function Module({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return <div className={cx('module', className)}>{children}</div>;
}

/**
 * Uppercase, tracked-out section header — the dashboard idiom.
 *
 * The uppercasing is `text-transform` in `.t-label-small`, never
 * `text.toUpperCase()` in here. That keeps the real, translated case in the DOM,
 * and therefore in the accessibility tree and in anything copied out of the
 * page: a screen reader announcing a shouted string is not the same experience
 * as one reading a label. It also matters for the two locales where an
 * uppercase mapping is not the identity round-trip a naive `toUpperCase()`
 * assumes.
 *
 * `<h2>` rather than a `<div>`: these labels are the only structure a
 * screen-reader user has for skipping between the blocks of a long dashboard,
 * and `.module__label` already resets the browser's heading margin because that
 * is what it was written for. Wraps to two lines before it clips (CSS) — a
 * tracked-out uppercase label runs long in Portuguese, and truncating a section
 * header loses the only cue to what the block underneath is.
 */
export function ModuleLabel({ text, className }: { text: string; className?: string }): JSX.Element {
  return <h2 className={cx('t-label-small module__label clamp-2', className)}>{text}</h2>;
}

/**
 * The same label placed *inside* its module, sharing the left edge of the
 * content it heads.
 *
 * The distinction is not cosmetic. A bare {@link ModuleLabel} sits in the
 * screen's own column so it lines up with the screen padding, while a module's
 * own text is indented a further 16px — header and content then never share an
 * edge, and the eye reads that as two unrelated columns of text. Padding is
 * lg/lg/md/sm, straight from `Module.kt`.
 */
export function ModuleHeader({ text }: { text: string }): JSX.Element {
  return <ModuleLabel text={text} className="module__header" />;
}

/**
 * A hairline rule between rows of a module.
 *
 * Hidden from assistive technology: Compose's `HorizontalDivider` carries no
 * semantics either, and announcing "separator" between every one of four result
 * rows is noise, not structure.
 */
export function ModuleDivider(): JSX.Element {
  return <div className="module__divider" aria-hidden="true" />;
}

/**
 * A row of statistics divided by vertical hairlines.
 *
 * Each cell takes an equal share and wraps its own label, so a long translation
 * grows the row's height instead of squeezing its neighbours. Compose needed
 * `height(IntrinsicSize.Min)` for the dividers to match that height; the CSS
 * equivalent is `align-items: stretch` on the flex row, which lets each divider
 * take the height of the tallest cell for free. Either way the failure being
 * avoided is the same: a fixed-height rule leaves the first Portuguese label
 * that wraps to two lines with visibly short separators beside it.
 */
export function StatRow({ children }: { children: ReactNode }): JSX.Element {
  return <div className="stat-row">{children}</div>;
}

/**
 * One statistic: a number over its label.
 *
 * The number never truncates — it is the entire point of the cell — and its
 * label may, to two lines. Compose's `valueColor: Color = onSurface` parameter
 * is spelled `className="c-alive"` here; see the note at the top of this file
 * for why the colour arrives as a utility class rather than as a token string.
 */
export function StatCell({
  value,
  label,
  className,
}: {
  value: string;
  label: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={cx('stat-cell', className)}>
      <div className="n-large stat-cell__value">{value}</div>
      <div className="t-label-small stat-cell__label clamp-2">{label}</div>
    </div>
  );
}

/** The vertical hairline between two {@link StatCell}s. Decorative — see {@link ModuleDivider}. */
export function StatCellDivider(): JSX.Element {
  return <div className="stat-cell-divider" aria-hidden="true" />;
}

/**
 * Label on the left, value on the right, sharing a baseline.
 *
 * Android lays this out by hand rather than with weights, and the CSS grid here
 * reproduces that outcome rather than the mechanism. The reasoning is worth
 * carrying over intact, because the obvious implementation is the wrong one:
 * `weight(1f, fill = false)` on the label plus `weight(1f)` on the value — or,
 * here, `flex: 0 1 auto` plus `flex: 1` — looks like "the label yields space",
 * but the space it yields is not handed to the value. It is stranded in the
 * middle of the row. Every row has a different-length label, so every row ends
 * up with a differently-sized hole, and a column of them reads as ragged even
 * though each row is individually correct.
 *
 * So the label is measured first and capped — `fit-content(45%)`, exactly
 * `LabelValueLayout.LABEL_MAX_FRACTION` — and the value gets what is genuinely
 * left. Past that cap the value has nowhere to wrap and is pushed off the right
 * edge, which is what happens in Portuguese, where labels run about a third
 * longer than the Vietnamese they were laid out against. The value wraps to
 * three lines and both texts hang from their first baseline (`align-items:
 * baseline`); top-aligning instead makes a three-line value sit visibly higher
 * than its one-line label.
 *
 * A `.c-*` class tints the value only — see the note at the top of this file.
 */
export function LabelValueRow({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={cx('label-value-row', className)}>
      <div className="t-body-medium label-value-row__label break-anywhere">{label}</div>
      <div className="t-body-medium label-value-row__value clamp-3 break-anywhere">{value}</div>
    </div>
  );
}
